import { useState, useCallback, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  Plus,
  Trash2,
  Save,
  TrendingUp,
  ChevronDown,
  ChevronRight,
  X,
} from "lucide-react";
import { useDealContext } from "../hooks/useDealContext";
import { updateDeal, autoProject, fetchDeal, type MarketLeasingProfile, type Deal } from "../api/client";
import { useToastContext } from "../contexts/ToastContext";
import EmptyState from "../components/EmptyState";
import LoadingSpinner from "../components/LoadingSpinner";
import { fmtDate, fmtNumber, fmtPSF } from "../utils/format";

/** Generate a short random ID for new profiles. */
function newId(): string {
  return "mlp_" + Math.random().toString(36).slice(2, 10);
}

/** Default new profile values. */
function defaultProfile(name: string): MarketLeasingProfile {
  return {
    profile_id: newId(),
    profile_name: name,
    market_rent_psf: 20,
    lease_term_years: 5,
    annual_rent_increase_pct: 0.03,
    reimbursement_method: "continue_prior",
    new_tenant_ti_psf: 15,
    renewal_tenant_ti_psf: 5,
    leasing_commissions_new_pct: 0.06,
    leasing_commissions_renewal_pct: 0.02,
    downtime_months: 6,
    renewal_probability_pct: 0.6,
    renewal_rent_basis: "expiring",
    renewal_market_rent_pct: 1.0,
    renewal_downtime_months: 0,
    new_tenant_free_rent_months: 0,
    renewal_free_rent_months: 0,
    upon_expiration: "roll_to_market",
  };
}

const REIMBURSEMENT_OPTIONS = [
  { value: "continue_prior", label: "Continue Prior Lease Type" },
  { value: "NNN", label: "Triple Net (NNN)" },
  { value: "gross", label: "Gross" },
  { value: "modified_gross", label: "Modified Gross" },
];

const EXPIRATION_OPTIONS = [
  { value: "roll_to_market", label: "Roll to Market" },
  { value: "expire", label: "Expire (Stay Vacant)" },
];

export default function MarketLeasingPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { deal, setDeal } = useDealContext();
  const { addToast } = useToastContext();
  const [autoProjecting, setAutoProjecting] = useState(false);
  const hasTriedRef = useRef(false);

  useEffect(() => {
    if (deal.property_data || !dealId || hasTriedRef.current) return;
    hasTriedRef.current = true;
    setAutoProjecting(true);
    autoProject(dealId)
      .then(() => fetchDeal(dealId))
      .then(setDeal)
      .catch(() => {})
      .finally(() => setAutoProjecting(false));
  }, [deal.property_data, dealId, setDeal]);

  const prop = deal.property_data;

  const profiles: MarketLeasingProfile[] =
    prop?.market_leasing_profiles ?? [];
  const tenants = prop?.tenants ?? [];

  const [selectedId, setSelectedId] = useState<string | null>(
    profiles.length > 0 ? profiles[0].profile_id : null
  );
  // Sync selectedId when profiles load (e.g., after deal data arrives)
  useEffect(() => {
    if (!selectedId && profiles.length > 0) {
      setSelectedId(profiles[0].profile_id);
    }
  }, [profiles.length]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [showMonthly, setShowMonthly] = useState(false);

  const selected = profiles.find((p) => p.profile_id === selectedId) ?? null;

  /** Persist profiles + tenant assignments to the deal's property_data. */
  const save = useCallback(
    async (
      nextProfiles: MarketLeasingProfile[],
      tenantUpdates?: Record<string, string | null>
    ) => {
      if (!dealId || !prop) return;
      setSaving(true);
      try {
        const nextTenants = tenantUpdates
          ? prop.tenants.map((t) => {
              if (t.tenant_id in tenantUpdates) {
                return {
                  ...t,
                  market_leasing_profile_id:
                    tenantUpdates[t.tenant_id] ?? undefined,
                };
              }
              return t;
            })
          : prop.tenants;

        const nextProp = {
          ...prop,
          market_leasing_profiles: nextProfiles,
          tenants: nextTenants,
        };
        const updated = await updateDeal(dealId, {
          property_data: nextProp,
        });
        setDeal(updated as Deal);
        setDirty(false);
      } catch (err) {
        console.error("Failed to save market leasing profiles:", err);
        addToast("Failed to save. Check console for details.", "error");
      } finally {
        setSaving(false);
      }
    },
    [dealId, prop, setDeal]
  );

  /** Update a single profile field in local state and mark dirty. */
  function updateProfile(
    profileId: string,
    field: string,
    value: unknown
  ) {
    if (!prop) return;
    const next = profiles.map((p) =>
      p.profile_id === profileId ? { ...p, [field]: value } : p
    );
    // Optimistically update local deal state
    setDeal((prev) => {
      if (!prev?.property_data) return prev;
      return {
        ...prev,
        property_data: { ...prev.property_data, market_leasing_profiles: next },
      } as Deal;
    });
    setDirty(true);
  }

  function addProfile() {
    const name = `Profile ${profiles.length + 1}`;
    const np = defaultProfile(name);
    const next = [...profiles, np];
    setDeal((prev) => {
      if (!prev?.property_data) return prev;
      return {
        ...prev,
        property_data: { ...prev.property_data, market_leasing_profiles: next },
      } as Deal;
    });
    setSelectedId(np.profile_id);
    setDirty(true);
  }

  function deleteProfile(id: string) {
    const next = profiles.filter((p) => p.profile_id !== id);
    // Unassign tenants that used this profile
    const tenantUpdates: Record<string, string | null> = {};
    for (const t of tenants) {
      if (t.market_leasing_profile_id === id) {
        tenantUpdates[t.tenant_id] = null;
      }
    }
    setDeal((prev) => {
      if (!prev?.property_data) return prev;
      const nextTenants = prev.property_data.tenants.map((t) =>
        t.market_leasing_profile_id === id
          ? { ...t, market_leasing_profile_id: undefined }
          : t
      );
      return {
        ...prev,
        property_data: {
          ...prev.property_data,
          market_leasing_profiles: next,
          tenants: nextTenants,
        },
      } as Deal;
    });
    if (selectedId === id) {
      setSelectedId(next.length > 0 ? next[0].profile_id : null);
    }
    save(next, tenantUpdates);
  }

  function assignTenant(tenantId: string, profileId: string | null) {
    setDeal((prev) => {
      if (!prev?.property_data) return prev;
      const nextTenants = prev.property_data.tenants.map((t) =>
        t.tenant_id === tenantId
          ? { ...t, market_leasing_profile_id: profileId ?? undefined }
          : t
      );
      return {
        ...prev,
        property_data: { ...prev.property_data, tenants: nextTenants },
      } as Deal;
    });
    save(profiles, { [tenantId]: profileId });
  }

  if (autoProjecting) return <LoadingSpinner />;

  if (!prop) {
    return (
      <EmptyState
        icon={TrendingUp}
        title="No property data"
        description="Upload and parse a rent roll to configure market leasing assumptions."
      />
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold text-bark-800">
            Market Leasing Assumptions
          </h2>
          <p className="text-sm text-bark-400 mt-1">
            Define leasing profiles for rollover and assign them to tenants
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={addProfile}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all shadow-md"
          >
            <Plus className="w-4 h-4" />
            New Profile
          </button>
          {dirty && (
            <button
              onClick={() => save(profiles)}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-forest-700 text-white text-sm font-semibold rounded-lg hover:bg-forest-600 disabled:opacity-50 transition-all shadow-md"
            >
              <Save className="w-4 h-4" />
              {saving ? "Saving..." : "Save"}
            </button>
          )}
        </div>
      </div>

      {profiles.length === 0 ? (
        <EmptyState
          icon={TrendingUp}
          title="No leasing profiles"
          description="Create a market leasing profile to define rollover assumptions for tenants."
          action={
            <button
              onClick={addProfile}
              className="inline-flex items-center gap-2 px-6 py-3 bg-honey-600 text-white text-sm font-semibold rounded-lg hover:bg-honey-500 hover:shadow-lg transition-all shadow-md"
            >
              <Plus className="w-4 h-4" />
              Create Profile
            </button>
          }
        />
      ) : (
        <div className="grid grid-cols-12 gap-6">
          {/* Profile list sidebar */}
          <div className="col-span-3">
            <div className="bg-white rounded-xl border border-grove-300 shadow-sm divide-y divide-grove-200">
              {profiles.map((p) => (
                <button
                  key={p.profile_id}
                  onClick={() => setSelectedId(p.profile_id)}
                  className={`w-full text-left px-4 py-3 flex items-center justify-between transition-colors ${
                    selectedId === p.profile_id
                      ? "bg-forest-50 border-l-2 border-forest-700"
                      : "hover:bg-grove-50 border-l-2 border-transparent"
                  }`}
                >
                  <div className="min-w-0">
                    <p
                      className={`text-sm font-medium truncate ${
                        selectedId === p.profile_id
                          ? "text-forest-700"
                          : "text-bark-700"
                      }`}
                    >
                      {p.profile_name}
                    </p>
                    <p className="text-[11px] text-bark-400 mt-0.5 tabular-nums">
                      {fmtPSF(p.market_rent_psf)} &middot;{" "}
                      {p.lease_term_years}yr
                    </p>
                  </div>
                  {confirmDeleteId === p.profile_id ? (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteProfile(p.profile_id);
                        setConfirmDeleteId(null);
                      }}
                      onBlur={() => setTimeout(() => setConfirmDeleteId(null), 2000)}
                      className="px-2 py-0.5 text-[10px] font-semibold text-white bg-red-600 rounded hover:bg-red-700 transition-colors flex-shrink-0"
                    >
                      Confirm
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmDeleteId(p.profile_id);
                      }}
                      className="p-1 text-bark-300 hover:text-red-600 rounded hover:bg-red-50 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Profile detail editor */}
          <div className="col-span-9">
            {selected ? (
              <ProfileEditor
                profile={selected}
                onChange={(field, value) =>
                  updateProfile(selected.profile_id, field, value)
                }
                showMonthly={showMonthly}
                setShowMonthly={setShowMonthly}
              />
            ) : (
              <div className="bg-white rounded-xl border border-grove-300 p-12 text-center text-bark-400">
                Select a profile to edit
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tenant Assignment */}
      {profiles.length > 0 && tenants.length > 0 && (
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Tenant Profile Assignment
          </h3>
          <div className="bg-white rounded-xl border border-grove-300 overflow-x-auto shadow-sm">
            <table className="w-full data-table text-[13px]">
              <thead>
                <tr>
                  <th className="sticky top-0">Tenant</th>
                  <th className="sticky top-0">Suite</th>
                  <th className="num sticky top-0">SF</th>
                  <th className="sticky top-0">Expiration</th>
                  <th className="sticky top-0 min-w-[200px]">
                    Leasing Profile
                  </th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((t) => (
                  <tr key={t.tenant_id}>
                    <td className="font-medium text-bark-700 whitespace-nowrap">
                      {t.tenant_name}
                    </td>
                    <td className="text-bark-400 whitespace-nowrap">
                      {t.suite_id || "\u2014"}
                    </td>
                    <td className="num tabular-nums">{fmtNumber(t.leased_sf)}</td>
                    <td className="whitespace-nowrap tabular-nums">
                      {fmtDate(t.lease_expiration_date)}
                    </td>
                    <td>
                      <select
                        value={t.market_leasing_profile_id || ""}
                        onChange={(e) =>
                          assignTenant(
                            t.tenant_id,
                            e.target.value || null
                          )
                        }
                        className="w-full px-3 py-1.5 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
                        data-field-id="market_leasing_profile_id"
                        data-tenant={t.tenant_name}
                      >
                        <option value="">No Profile (use deal defaults)</option>
                        {profiles.map((p) => (
                          <option key={p.profile_id} value={p.profile_id}>
                            {p.profile_name}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Rollover Summary */}
          <div className="mt-6 bg-white rounded-xl border border-grove-300 p-5 shadow-sm">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-bark-400 mb-3">
              Rollover Summary
            </h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              {(() => {
                const totalSf = tenants.reduce((s, t) => s + t.leased_sf, 0);
                let wtdRent = 0;
                let wtdTerm = 0;
                let assignedCount = 0;
                for (const t of tenants) {
                  const p = t.market_leasing_profile_id
                    ? profiles.find((pr) => pr.profile_id === t.market_leasing_profile_id)
                    : profiles[0];
                  if (p) {
                    wtdRent += (p.market_rent_psf ?? 0) * t.leased_sf;
                    wtdTerm += (p.lease_term_years ?? 0) * t.leased_sf;
                  }
                  if (t.market_leasing_profile_id) assignedCount++;
                }
                const avgRent = totalSf > 0 ? wtdRent / totalSf : 0;
                const avgTerm = totalSf > 0 ? wtdTerm / totalSf : 0;
                return (
                  <>
                    <div>
                      <span className="text-bark-400">Wtd Avg Market Rent</span>
                      <span className="block text-lg font-semibold text-bark-700 tabular-nums">
                        {fmtPSF(avgRent)}
                      </span>
                    </div>
                    <div>
                      <span className="text-bark-400">Wtd Avg Lease Term</span>
                      <span className="block text-lg font-semibold text-bark-700 tabular-nums">
                        {avgTerm.toFixed(1)} yrs
                      </span>
                    </div>
                    <div>
                      <span className="text-bark-400">Assigned Tenants</span>
                      <span className="block text-lg font-semibold text-bark-700 tabular-nums">
                        {assignedCount} / {tenants.length}
                      </span>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Input row helper for the profile editor. */
function FieldRow({
  label,
  sublabel,
  fieldId,
  children,
}: {
  label: string;
  sublabel?: string;
  /** data-field-id anchor for the chat change walkthrough highlighter. */
  fieldId?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-start gap-4" data-field-id={fieldId || undefined}>
      <div className="w-[220px] flex-shrink-0 pt-2">
        <label className="text-sm font-medium text-bark-600">{label}</label>
        {sublabel && (
          <p className="text-[11px] text-bark-400 mt-0.5">{sublabel}</p>
        )}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function NumInput({
  value,
  onChange,
  step,
  min,
  suffix,
  prefix,
}: {
  value: number;
  onChange: (v: number) => void;
  step?: number;
  min?: number;
  suffix?: string;
  prefix?: string;
}) {
  return (
    <div className="relative">
      {prefix && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-bark-400 text-sm pointer-events-none">
          {prefix}
        </span>
      )}
      <input
        type="number"
        step={step ?? 1}
        min={min ?? 0}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        className={`w-full px-3 py-2 text-sm bg-white border border-grove-300 rounded-lg text-bark-700 tabular-nums ${
          prefix ? "pl-7" : ""
        } ${suffix ? "pr-10" : ""}`}
      />
      {suffix && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-bark-400 text-xs pointer-events-none">
          {suffix}
        </span>
      )}
    </div>
  );
}

function ProfileEditor({
  profile,
  onChange,
  showMonthly,
  setShowMonthly,
}: {
  profile: MarketLeasingProfile;
  onChange: (field: string, value: unknown) => void;
  showMonthly: boolean;
  setShowMonthly: (v: boolean) => void;
}) {
  return (
    <div className="bg-white rounded-xl border border-grove-300 shadow-sm">
      {/* Profile name */}
      <div className="px-6 py-4 border-b border-grove-200">
        <input
          value={profile.profile_name}
          onChange={(e) => onChange("profile_name", e.target.value)}
          className="text-lg font-display font-semibold text-bark-800 bg-transparent border-none outline-none w-full"
          placeholder="Profile Name"
          data-field-id="profile_name"
        />
      </div>

      <div className="px-6 py-5 space-y-5">
        {/* Market Rent */}
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Rent Assumptions
          </h4>
          <div className="space-y-3">
            <FieldRow label="Market Rent" sublabel="Per square foot / year" fieldId="market_rent_psf">
              <NumInput
                value={profile.market_rent_psf}
                onChange={(v) => onChange("market_rent_psf", v)}
                step={0.5}
                prefix="$"
                suffix="/SF"
              />
            </FieldRow>
            <FieldRow
              label="Annual Rent Increase"
              sublabel="Applied each year of the lease"
              fieldId="annual_rent_increase_pct"
            >
              <NumInput
                value={+(profile.annual_rent_increase_pct * 100).toFixed(2)}
                onChange={(v) =>
                  onChange("annual_rent_increase_pct", v / 100)
                }
                step={0.1}
                suffix="%"
              />
            </FieldRow>
            <FieldRow label="Lease Term" fieldId="lease_term_years">
              <NumInput
                value={profile.lease_term_years}
                onChange={(v) => onChange("lease_term_years", v)}
                step={1}
                min={1}
                suffix="years"
              />
            </FieldRow>

            {/* Monthly rent schedule toggle */}
            <div className="pt-1">
              <button
                onClick={() => setShowMonthly(!showMonthly)}
                className="flex items-center gap-1.5 text-xs font-medium text-forest-700 hover:text-forest-600 transition-colors"
              >
                {showMonthly ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                Month-by-Month Rent Schedule
              </button>
              {showMonthly && (
                <MonthlyScheduleEditor
                  profile={profile}
                  onChange={onChange}
                />
              )}
            </div>
          </div>
        </div>

        {/* Reimbursement */}
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Reimbursement
          </h4>
          <FieldRow label="Reimbursement Method" sublabel="For new/renewed leases" fieldId="reimbursement_method">
            <select
              value={profile.reimbursement_method}
              onChange={(e) =>
                onChange("reimbursement_method", e.target.value)
              }
              className="w-full px-3 py-2 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
            >
              {REIMBURSEMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </FieldRow>
        </div>

        {/* TI & LC */}
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Tenant Improvements & Leasing Commissions
          </h4>
          <div className="space-y-3">
            <FieldRow label="New Tenant TI" sublabel="Per square foot" fieldId="new_tenant_ti_psf">
              <NumInput
                value={profile.new_tenant_ti_psf}
                onChange={(v) => onChange("new_tenant_ti_psf", v)}
                step={0.5}
                prefix="$"
                suffix="/SF"
              />
            </FieldRow>
            <FieldRow label="Renewal TI" sublabel="Per square foot" fieldId="renewal_tenant_ti_psf">
              <NumInput
                value={profile.renewal_tenant_ti_psf}
                onChange={(v) => onChange("renewal_tenant_ti_psf", v)}
                step={0.5}
                prefix="$"
                suffix="/SF"
              />
            </FieldRow>
            <FieldRow label="New LC" sublabel="% of first-year rent" fieldId="leasing_commissions_new_pct">
              <NumInput
                value={+(profile.leasing_commissions_new_pct * 100).toFixed(2)}
                onChange={(v) =>
                  onChange("leasing_commissions_new_pct", v / 100)
                }
                step={0.5}
                suffix="%"
              />
            </FieldRow>
            <FieldRow label="Renewal LC" sublabel="% of first-year rent" fieldId="leasing_commissions_renewal_pct">
              <NumInput
                value={
                  +(profile.leasing_commissions_renewal_pct * 100).toFixed(2)
                }
                onChange={(v) =>
                  onChange("leasing_commissions_renewal_pct", v / 100)
                }
                step={0.5}
                suffix="%"
              />
            </FieldRow>
          </div>
        </div>

        {/* Rollover */}
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-[0.06em] text-bark-400 mb-3">
            Rollover
          </h4>
          <div className="space-y-3">
            <FieldRow
              label="Renewal Probability"
              sublabel="Chance existing tenant renews"
              fieldId="renewal_probability_pct"
            >
              <NumInput
                value={+(profile.renewal_probability_pct * 100).toFixed(1)}
                onChange={(v) =>
                  onChange("renewal_probability_pct", v / 100)
                }
                step={5}
                suffix="%"
              />
            </FieldRow>
            <FieldRow
              label="Renewal Rent"
              sublabel="How renewal rent is set"
            >
              <select
                value={profile.renewal_rent_basis ?? "expiring"}
                onChange={(e) =>
                  onChange("renewal_rent_basis", e.target.value)
                }
                className="w-full px-3 py-2 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
              >
                <option value="expiring">Expiring Rent</option>
                <option value="market">Market Rent</option>
                <option value="higher_of">Higher of Expiring / Market</option>
                <option value="pct_of_market">% of Market</option>
              </select>
            </FieldRow>
            {profile.renewal_rent_basis === "pct_of_market" && (
              <FieldRow
                label="% of Market"
                sublabel="Renewal rent as % of market rent"
              >
                <NumInput
                  value={+((profile.renewal_market_rent_pct ?? 1.0) * 100).toFixed(1)}
                  onChange={(v) =>
                    onChange("renewal_market_rent_pct", v / 100)
                  }
                  step={5}
                  suffix="%"
                />
              </FieldRow>
            )}
            <FieldRow
              label="New Tenant Downtime"
              sublabel="Vacancy months if not renewed"
              fieldId="downtime_months"
            >
              <NumInput
                value={profile.downtime_months}
                onChange={(v) => onChange("downtime_months", v)}
                step={1}
                suffix="mo"
              />
            </FieldRow>
            <FieldRow
              label="Renewal Downtime"
              sublabel="Vacancy months if renewed"
              fieldId="renewal_downtime_months"
            >
              <NumInput
                value={profile.renewal_downtime_months}
                onChange={(v) => onChange("renewal_downtime_months", v)}
                step={1}
                suffix="mo"
              />
            </FieldRow>
            <FieldRow
              label="Upon Expiration"
              sublabel="What happens when the lease expires"
              fieldId="upon_expiration"
            >
              <select
                value={profile.upon_expiration}
                onChange={(e) =>
                  onChange("upon_expiration", e.target.value)
                }
                className="w-full px-3 py-2 text-sm bg-white border border-grove-300 rounded-lg text-bark-700"
              >
                {EXPIRATION_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </FieldRow>
          </div>
        </div>
      </div>
    </div>
  );
}

/** ARGUS-style month-by-month rent schedule popup/editor. */
function MonthlyScheduleEditor({
  profile,
  onChange,
}: {
  profile: MarketLeasingProfile;
  onChange: (field: string, value: unknown) => void;
}) {
  const totalMonths = profile.lease_term_years * 12;
  const schedule = profile.monthly_rent_schedule ?? [];

  // Build a full month array from the schedule, filling gaps with the base rate
  const monthRents: number[] = [];
  for (let m = 1; m <= totalMonths; m++) {
    const entry = schedule.find((e) => e.month === m);
    if (entry) {
      monthRents.push(entry.rent_annual_psf);
    } else {
      // Use base market rent, escalated by year
      const yr = Math.floor((m - 1) / 12);
      monthRents.push(
        +(
          profile.market_rent_psf *
          Math.pow(1 + profile.annual_rent_increase_pct, yr)
        ).toFixed(2)
      );
    }
  }

  function setMonth(month: number, rent: number) {
    const next = schedule.filter((e) => e.month !== month);
    next.push({ month, rent_annual_psf: rent });
    next.sort((a, b) => a.month - b.month);
    onChange("monthly_rent_schedule", next);
  }

  function clearSchedule() {
    onChange("monthly_rent_schedule", undefined);
  }

  return (
    <div className="mt-3 bg-grove-50 border border-grove-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-bark-500">
          Override rent for specific months. Empty cells use the auto-escalated
          rate.
        </p>
        {schedule.length > 0 && (
          <button
            onClick={clearSchedule}
            className="text-xs text-red-600 hover:text-red-700 flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Clear All
          </button>
        )}
      </div>
      <div className="max-h-[300px] overflow-y-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-bark-400">
              <th className="text-left pb-1 pr-4 font-medium">Month</th>
              <th className="text-left pb-1 pr-4 font-medium">Year</th>
              <th className="text-right pb-1 font-medium">
                Rent PSF (Annual)
              </th>
            </tr>
          </thead>
          <tbody>
            {monthRents.map((rent, idx) => {
              const month = idx + 1;
              const yr = Math.floor(idx / 12) + 1;
              const hasOverride = schedule.some((e) => e.month === month);
              return (
                <tr
                  key={month}
                  className={
                    hasOverride ? "bg-honey-50" : idx % 2 === 0 ? "bg-white" : ""
                  }
                >
                  <td className="py-1 pr-4 text-bark-600 tabular-nums">
                    {month}
                  </td>
                  <td className="py-1 pr-4 text-bark-400 tabular-nums">
                    Yr {yr}
                  </td>
                  <td className="py-1 text-right">
                    <input
                      type="number"
                      step={0.25}
                      min={0}
                      value={rent}
                      onChange={(e) =>
                        setMonth(month, parseFloat(e.target.value) || 0)
                      }
                      className={`w-24 px-2 py-1 text-right text-xs border rounded tabular-nums ${
                        hasOverride
                          ? "border-honey-400 bg-honey-50 text-bark-700 font-medium"
                          : "border-grove-200 bg-white text-bark-500"
                      }`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
